// Public Marketplace listing page — Phase 1.
//
// Mirrors the dark/teal premium aesthetic of the rest of unisol.global:
// hero on slate-900 with teal accents, listing cards on slate-50, sticky
// filter rail on desktop.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Search, SlidersHorizontal, Fuel, Gauge, Cog, MapPin, ChevronDown,
  ArrowRight, ShieldCheck, BadgeCheck, X,
} from 'lucide-react';
import { estimateMonthly, fmtInr } from '../lib/emi';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FUEL_LABEL = {
  petrol: 'Petrol', diesel: 'Diesel', cng: 'CNG',
  electric: 'Electric', hybrid: 'Hybrid',
};
const TRANS_LABEL = {
  manual: 'Manual', automatic: 'Automatic',
  amt: 'AMT', cvt: 'CVT', dct: 'DCT',
};

const inr = (n) => {
  if (!n && n !== 0) return '—';
  if (n >= 10_000_00) return `₹${(n / 100000).toFixed(1)} L`;
  return `₹${n.toLocaleString('en-IN')}`;
};
const km = (n) => (n || n === 0) ? `${n.toLocaleString('en-IN')} km` : '—';

const Marketplace = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    q: '', body_type: '', fuel_type: '', transmission: '',
    year_min: '', year_max: '', price_min: '', price_max: '',
    km_max: '', sort: 'newest', page: 1, limit: 24,
  });

  const queryString = useMemo(() => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== '' && v !== null && v !== undefined) p.set(k, v);
    });
    return p.toString();
  }, [filters]);

  const fetchCars = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/marketplace/cars?${queryString}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      console.error('Marketplace fetch failed', e);
    } finally {
      setLoading(false);
    }
  }, [queryString]);

  useEffect(() => { fetchCars(); }, [fetchCars]);

  const updateFilter = (k, v) => setFilters((p) => ({ ...p, [k]: v, page: 1 }));
  const resetFilters = () => setFilters({
    q: '', body_type: '', fuel_type: '', transmission: '',
    year_min: '', year_max: '', price_min: '', price_max: '',
    km_max: '', sort: 'newest', page: 1, limit: 24,
  });

  return (
    <div className="min-h-screen bg-slate-50 pt-24">
      {/* ===== Hero ===== */}
      <section className="relative isolate overflow-hidden text-white">
        {/* Layered backgrounds: solid base + radial highlight + subtle grain.
            Keeping it on a darker slate base than before, with a teal glow
            from the bottom-right, gives the H1 a confident, readable surface. */}
        <div className="absolute inset-0 -z-10 bg-[#0b1722]" />
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(70%_60%_at_85%_85%,rgba(20,184,166,0.28),transparent_60%),radial-gradient(50%_60%_at_15%_10%,rgba(56,189,248,0.10),transparent_60%)]" />
        <div className="absolute inset-0 -z-10 opacity-[0.04] mix-blend-overlay"
             style={{ backgroundImage: 'url("data:image/svg+xml;utf8,<svg xmlns=%27http://www.w3.org/2000/svg%27 width=%27200%27 height=%27200%27><filter id=%27n%27><feTurbulence type=%27fractalNoise%27 baseFrequency=%270.85%27 numOctaves=%272%27 stitchTiles=%27stitch%27/></filter><rect width=%27100%25%27 height=%27100%25%27 filter=%27url(%23n)%27/></svg>")' }} />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="inline-flex items-center gap-2 text-teal-300 text-[11px] uppercase tracking-[0.22em] font-semibold mb-5 bg-teal-500/10 border border-teal-500/20 px-3 py-1 rounded-full">
            <ShieldCheck size={13} /> Pre-owned · Inspected · Detailed
          </div>
          <h1 className="text-white text-3xl sm:text-5xl lg:text-[64px] font-semibold tracking-tight leading-[1.08] max-w-4xl">
            Buy a <span className="text-teal-300">pre-owned car</span><br/>
            ready for the road, by Unisol.
          </h1>
          <p className="mt-5 sm:mt-6 text-slate-200 max-w-2xl text-sm sm:text-lg leading-relaxed">
            Every car listed here is inspected by our detailing studio — documented condition, PPF / ceramic options at handover, financing &amp; insurance support, and trade-in offers on your old car.
          </p>

          {/* Quick search + Sell CTA */}
          <div className="mt-9 flex flex-col sm:flex-row gap-3 max-w-3xl">
            <div className="relative flex-1">
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                type="text"
                value={filters.q}
                onChange={(e) => updateFilter('q', e.target.value)}
                placeholder="Search make, model or variant (e.g. Honda City)"
                className="w-full bg-white text-slate-900 placeholder-slate-400 pl-12 pr-4 py-3.5 rounded-xl text-sm sm:text-base outline-none focus:ring-2 focus:ring-teal-300 shadow-xl shadow-black/30"
                data-testid="marketplace-search"
              />
            </div>
            <Link
              to="/sell-your-car"
              className="bg-teal-400 hover:bg-teal-300 text-slate-900 font-semibold px-5 py-3.5 rounded-xl inline-flex items-center justify-center gap-2 shadow-lg shadow-teal-500/20 whitespace-nowrap"
              data-testid="hero-sell-cta"
            >
              Sell your car →
            </Link>
            <button
              onClick={() => setShowFilters((s) => !s)}
              className="sm:hidden bg-white/10 border border-white/15 text-white font-semibold px-5 py-3.5 rounded-xl inline-flex items-center justify-center gap-2"
              data-testid="marketplace-filters-toggle"
            >
              <SlidersHorizontal size={16} /> Filters
            </button>
          </div>

          {/* Inline pillars */}
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-6 max-w-3xl text-sm">
            {[
              { icon: BadgeCheck, label: 'Verified by Unisol' },
              { icon: ShieldCheck, label: 'PPF / Ceramic at handover' },
              { icon: Cog, label: 'Finance & Insurance' },
              { icon: ArrowRight, label: 'Trade in your old car' },
            ].map((it) => (
              <div key={it.label} className="flex items-center gap-2.5">
                <span className="w-8 h-8 inline-flex items-center justify-center bg-teal-500/15 border border-teal-400/30 rounded-full shrink-0">
                  <it.icon size={15} className="text-teal-300" />
                </span>
                <span className="font-medium text-[13px] sm:text-sm text-white">{it.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Body ===== */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Filter rail */}
          <aside className={`${showFilters ? 'block' : 'hidden'} lg:block lg:w-72 lg:sticky lg:top-28 lg:self-start`}>
            <div className="bg-white border border-slate-200 rounded-2xl p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wider">Filters</h3>
                <button onClick={resetFilters} className="text-xs text-teal-700 hover:underline" data-testid="filter-reset">
                  Reset
                </button>
              </div>

              <Group label="Body type">
                <Chips
                  options={['hatchback', 'sedan', 'suv', 'muv', 'coupe']}
                  value={filters.body_type}
                  onChange={(v) => updateFilter('body_type', v)}
                />
              </Group>

              <Group label="Fuel">
                <Chips
                  options={Object.keys(FUEL_LABEL)}
                  labelMap={FUEL_LABEL}
                  value={filters.fuel_type}
                  onChange={(v) => updateFilter('fuel_type', v)}
                />
              </Group>

              <Group label="Transmission">
                <Chips
                  options={Object.keys(TRANS_LABEL)}
                  labelMap={TRANS_LABEL}
                  value={filters.transmission}
                  onChange={(v) => updateFilter('transmission', v)}
                />
              </Group>

              <Group label="Year">
                <div className="flex gap-2">
                  <NumberInput value={filters.year_min} onChange={(v) => updateFilter('year_min', v)} placeholder="From" testid="year-min" />
                  <NumberInput value={filters.year_max} onChange={(v) => updateFilter('year_max', v)} placeholder="To" testid="year-max" />
                </div>
              </Group>

              <Group label="Price (₹)">
                <div className="flex gap-2">
                  <NumberInput value={filters.price_min} onChange={(v) => updateFilter('price_min', v)} placeholder="Min" testid="price-min" />
                  <NumberInput value={filters.price_max} onChange={(v) => updateFilter('price_max', v)} placeholder="Max" testid="price-max" />
                </div>
              </Group>

              <Group label="Max KM">
                <NumberInput value={filters.km_max} onChange={(v) => updateFilter('km_max', v)} placeholder="e.g. 60000" testid="km-max" />
              </Group>

              <button onClick={() => setShowFilters(false)} className="lg:hidden mt-5 w-full bg-slate-900 text-white font-semibold py-2.5 rounded-lg inline-flex items-center justify-center gap-2">
                <X size={14} /> Close filters
              </button>
            </div>
          </aside>

          {/* Results */}
          <div className="flex-1 min-w-0">
            {/* Toolbar */}
            <div className="flex items-center justify-between mb-6 gap-3">
              <p className="text-sm text-slate-600">
                {loading ? 'Loading…' : `${total} ${total === 1 ? 'car' : 'cars'} available`}
              </p>
              <div className="relative">
                <select
                  value={filters.sort}
                  onChange={(e) => updateFilter('sort', e.target.value)}
                  className="appearance-none bg-white border border-slate-300 rounded-lg pl-3 pr-9 py-2 text-sm font-medium text-slate-700 outline-none focus:ring-2 focus:ring-teal-400"
                  data-testid="marketplace-sort"
                >
                  <option value="newest">Newest first</option>
                  <option value="price_asc">Price: low to high</option>
                  <option value="price_desc">Price: high to low</option>
                  <option value="km_asc">Lowest KM first</option>
                </select>
                <ChevronDown size={16} className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400" />
              </div>
            </div>

            {loading ? (
              <SkeletonGrid />
            ) : items.length === 0 ? (
              <EmptyState onReset={resetFilters} />
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                  {items.map((car) => (
                    <CarCard key={car.id} car={car} />
                  ))}
                </div>

                {/* Pagination */}
                {total > filters.limit && (
                  <Pagination
                    page={filters.page}
                    limit={filters.limit}
                    total={total}
                    onChange={(p) => setFilters((f) => ({ ...f, page: p }))}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </section>

      {/* ===== Trust strip ===== */}
      <section className="bg-[#0b1722] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight max-w-3xl">
            Why Unisol&apos;s pre-owned isn&apos;t like the rest.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-8">
            {[
              { title: 'Pre-Delivery Inspection', body: 'Every car goes through the same multi-point inspection we use for new-car PPF jobs.' },
              { title: 'Detailing Add-ons', body: 'Add PPF, ceramic coating or interior detailing to your purchase at studio rates.' },
              { title: 'Trade-in & Finance Help', body: 'We connect you with our financing & insurance partners — no platform fees.' },
            ].map((b) => (
              <div key={b.title} className="bg-white/[0.03] border border-white/10 rounded-2xl p-6 hover:bg-white/[0.05] transition">
                <h3 className="text-lg font-semibold text-teal-300">{b.title}</h3>
                <p className="mt-2 text-slate-200 text-sm leading-relaxed">{b.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

// ───────────────────────────────────────────────────────── card + helpers ──
const CarCard = ({ car }) => {
  const title = [car.manufacture_year, car.make, car.model].filter(Boolean).join(' ');
  return (
    <Link
      to={`/marketplace/${car.id}`}
      className="group bg-white border border-slate-200 rounded-2xl overflow-hidden hover:border-teal-400 hover:shadow-xl transition-all duration-200"
      data-testid={`car-card-${car.id}`}
    >
      <div className="aspect-[4/3] bg-slate-100 overflow-hidden relative">
        {car.cover_photo ? (
          <img
            src={car.cover_photo}
            alt={`${title}`}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
        ) : (
          <div className="flex items-center justify-center h-full text-slate-400 text-xs">
            No photo
          </div>
        )}
        {car.listing_type === 'unisol_inventory' && (
          <span className="absolute top-3 left-3 bg-teal-500 text-slate-900 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded">
            Unisol Inventory
          </span>
        )}
      </div>
      <div className="p-4">
        <p className="text-xs text-teal-700 font-semibold uppercase tracking-wider">{car.variant || '\u00A0'}</p>
        <h3 className="text-base font-semibold text-slate-900 mt-0.5 leading-tight line-clamp-1">{title}</h3>
        <div className="flex items-center gap-3 mt-2.5 text-xs text-slate-500">
          <span className="inline-flex items-center gap-1"><Gauge size={12} /> {km(car.km_driven)}</span>
          <span className="inline-flex items-center gap-1"><Fuel size={12} /> {FUEL_LABEL[car.fuel_type] || car.fuel_type}</span>
          <span className="inline-flex items-center gap-1"><Cog size={12} /> {TRANS_LABEL[car.transmission] || car.transmission}</span>
        </div>
        <div className="flex items-end justify-between mt-4">
          <div>
            <p className="text-[11px] text-slate-400 uppercase tracking-wider">Price</p>
            <p className="text-xl font-bold text-slate-900">{inr(car.asking_price)}</p>
            {car.asking_price > 0 && (
              <p
                className="text-[11px] text-teal-700 font-semibold mt-0.5 inline-flex items-center gap-1"
                data-testid={`card-emi-${car.id}`}
                title="Estimated EMI at 20% down, 60 months, 14% p.a."
              >
                From ₹{fmtInr(estimateMonthly(car.asking_price))}/mo
              </p>
            )}
          </div>
          {car.city && (
            <span className="inline-flex items-center gap-1 text-xs text-slate-500">
              <MapPin size={12} /> {car.city}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
};

const Group = ({ label, children }) => (
  <div className="mb-5">
    <p className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">{label}</p>
    {children}
  </div>
);

const Chips = ({ options, value, onChange, labelMap }) => (
  <div className="flex flex-wrap gap-1.5">
    {options.map((opt) => {
      const active = value === opt;
      return (
        <button
          key={opt}
          type="button"
          onClick={() => onChange(active ? '' : opt)}
          className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
            active
              ? 'bg-teal-600 border-teal-600 text-white'
              : 'bg-white border-slate-300 text-slate-700 hover:border-teal-400'
          }`}
          data-testid={`chip-${opt}`}
        >
          {labelMap?.[opt] || (opt[0].toUpperCase() + opt.slice(1))}
        </button>
      );
    })}
  </div>
);

const NumberInput = ({ value, onChange, placeholder, testid }) => (
  <input
    type="number"
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    className="w-full bg-slate-50 border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-teal-400"
    data-testid={testid}
  />
);

const SkeletonGrid = () => (
  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
    {Array.from({ length: 6 }).map((_, i) => (
      <div key={`sk-${i}`} className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="aspect-[4/3] bg-slate-100 animate-pulse" />
        <div className="p-4 space-y-2">
          <div className="h-3 bg-slate-100 rounded w-1/3 animate-pulse" />
          <div className="h-4 bg-slate-100 rounded w-2/3 animate-pulse" />
          <div className="h-3 bg-slate-100 rounded w-1/2 animate-pulse mt-3" />
        </div>
      </div>
    ))}
  </div>
);

const EmptyState = ({ onReset }) => (
  <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-12 text-center">
    <p className="text-slate-700 font-semibold">No cars match these filters yet.</p>
    <p className="text-sm text-slate-500 mt-1">Try widening the search or check back soon — new cars are listed every week.</p>
    <button onClick={onReset} className="mt-5 inline-flex items-center gap-1 text-teal-700 font-semibold hover:underline" data-testid="empty-reset">
      Clear filters <ArrowRight size={14} />
    </button>
  </div>
);

const Pagination = ({ page, limit, total, onChange }) => {
  const pages = Math.ceil(total / limit);
  if (pages <= 1) return null;
  return (
    <div className="flex items-center justify-center gap-1.5 mt-10">
      {Array.from({ length: pages }).map((_, i) => (
        <button
          key={`p-${i}`}
          onClick={() => onChange(i + 1)}
          className={`w-9 h-9 rounded-lg text-sm font-medium ${
            page === i + 1
              ? 'bg-slate-900 text-white'
              : 'bg-white border border-slate-300 text-slate-700 hover:border-teal-400'
          }`}
          data-testid={`page-${i + 1}`}
        >
          {i + 1}
        </button>
      ))}
    </div>
  );
};

export default Marketplace;
